/**
 * GregorianDatePicker — drop-in replacement for `<input type="date">`.
 *
 * Why
 * ---
 * The native HTML date input ignores the `lang` attribute on
 * Saudi-region browsers (Chrome, Edge, Safari on macOS with the
 * Arabic-Saudi locale) and forces a Hijri popup calendar — even when
 * the displayed value text is Gregorian (yyyy/mm/dd). Merchants
 * reported repeatedly that "the field shows Gregorian but the popup
 * is Hijri".
 *
 * This component renders a styled trigger button whose label is always
 * a Gregorian dd/mm/yyyy string, and a Popover containing our
 * react-day-picker-based Calendar (which is also always Gregorian and
 * fully under our control).
 *
 * The wire format remains "YYYY-MM-DD" (the same string that
 * `<input type="date">` produces) so callers can swap their existing
 * input for this component without touching the API contract.
 */

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";

export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function fromIsoDate(s: string | undefined | null): Date | undefined {
  if (!s) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

export function formatGregorian(s: string | undefined | null): string {
  const d = fromIsoDate(s);
  if (!d) return "";
  return d.toLocaleDateString("en-GB-u-ca-gregory", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

interface GregorianDatePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  minDate?: string;
  maxDate?: string;
  allowClear?: boolean;
  testId?: string;
}

export function GregorianDatePicker({
  value,
  onChange,
  placeholder,
  className,
  disabled,
  minDate,
  maxDate,
  allowClear = true,
  testId,
}: GregorianDatePickerProps) {
  const { lang } = useI18n();
  const min = fromIsoDate(minDate);
  const max = fromIsoDate(maxDate);
  const calendarDisabled = (() => {
    if (min && max) return { before: min, after: max } as const;
    if (min) return { before: min } as const;
    if (max) return { after: max } as const;
    return undefined;
  })();
  const ph = placeholder ?? (lang === "ar" ? "اختر تاريخ" : "Pick a date");

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            className,
          )}
        >
          <span className="truncate">{value ? formatGregorian(value) : ph}</span>
          <span className="flex items-center gap-1">
            {value && allowClear && !disabled && (
              <span
                role="button"
                tabIndex={-1}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange("");
                }}
                className="hover:text-destructive p-0.5 rounded"
                aria-label={lang === "ar" ? "مسح" : "Clear"}
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <CalendarIcon className="h-4 w-4 opacity-60" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={fromIsoDate(value)}
          onSelect={(d) => onChange(d ? toIsoDate(d) : "")}
          disabled={calendarDisabled}
          initialFocus
          captionLayout="dropdown"
          fromYear={2000}
          toYear={new Date().getFullYear() + 5}
        />
      </PopoverContent>
    </Popover>
  );
}
